/**
 * RaisePlan Web UI - 主程式進入點
 *
 * 這是應用程式的起點，負責：
 * 1. 初始化配置
 * 2. 建立主應用程式實例
 * 3. 設定全域錯誤處理
 */

// 匯入樣式
import '@/styles/main.css';

// 匯入配置
import { CONFIG, ENV } from '@/config';

// 匯入認證 Store
import { loadAuthFromStorage, authStore } from '@/store/auth-store';

// 匯入 Session Manager
import { SessionManager } from '@/utils/session-manager';
import { Alert } from '@/components/shared/Alert';

// 匯入主應用程式
import { PlannerApp } from '@/app';

// 全域應用程式實例
let app: PlannerApp | null = null;

// 🔒 Session Manager 實例
let sessionManager: SessionManager | null = null;

/**
 * 初始化應用程式
 */
async function initializeApp(): Promise<void> {
  console.log('[RaisePlan] 應用程式初始化中...');
  console.log('[RaisePlan] API Base URL:', CONFIG.API_BASE_URL);
  console.log('[RaisePlan] 環境:', ENV.IS_LOCAL ? '開發' : '正式');

  // 從 localStorage 載入認證資料（在建立 App 之前）
  // 注意：必須 await，確保 token 在應用程式初始化前載入完成
  await loadAuthFromStorage();

  // 取得根容器
  const rootElement = document.getElementById('app');
  if (!rootElement) {
    throw new Error('找不到根元素 #app');
  }

  // 建立應用程式實例
  app = new PlannerApp(rootElement);

  console.log('[RaisePlan] 應用程式初始化完成');
}

/**
 * 全域錯誤處理
 */
function setupErrorHandling(): void {
  window.onerror = (message, source, lineno, colno, error) => {
    console.error('[RaisePlan] 未捕獲的錯誤:', {
      message,
      source,
      lineno,
      colno,
      error,
    });
    return false;
  };

  window.onunhandledrejection = (event) => {
    console.error('[RaisePlan] 未處理的 Promise 拒絕:', event.reason);
  };
}

/**
 * 🔒 初始化 Session Manager
 * 當使用者登入時啟動 Session 監控，登出時停止監控
 */
function setupSessionManagement(): void {
  authStore.subscribe((state) => {
    if (state.isAuthenticated && !sessionManager) {
      // 使用者已登入，啟動 Session Manager
      sessionManager = new SessionManager({
        inactivityTimeout: 30 * 60 * 1000, // 30 分鐘
        warningTime: 5 * 60 * 1000, // 提前 5 分鐘警告
        onWarning: () => {
          // 顯示警告通知
          const alert = new Alert({
            type: 'warning',
            title: 'Session 即將過期',
            message: '您的 Session 將在 5 分鐘後因不活動而過期，請進行操作以保持連線。',
            closable: true,
            autoClose: 0, // 不自動關閉
          });
          alert.show();
        },
        onTimeout: () => {
          // 顯示超時通知
          const alert = new Alert({
            type: 'error',
            title: 'Session 已過期',
            message: '由於長時間未活動，您的 Session 已過期，頁面將自動重新載入。',
            closable: false,
            autoClose: 3000,
          });
          alert.show();
        }
      });
      sessionManager.start();
      console.log('[RaisePlan] Session manager started');
    } else if (!state.isAuthenticated && sessionManager) {
      // 使用者已登出，停止 Session Manager
      sessionManager.destroy();
      sessionManager = null;
      console.log('[RaisePlan] Session manager stopped');
    }
  });
}

// 設定錯誤處理
setupErrorHandling();

// 🔒 設定 Session 管理
setupSessionManagement();

// DOM 載入完成後初始化應用程式
document.addEventListener('DOMContentLoaded', () => {
  initializeApp().catch((error) => {
    console.error('[RaisePlan] 初始化失敗:', error);
  });
});

// 開發模式下的 HMR 支援
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    // 銷毀舊實例並重新初始化
    app?.destroy();
    initializeApp().catch(console.error);
  });
}
